import React, { memo, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import SvgIcon from '@mui/material/SvgIcon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import MembreApi from '../api/MembreApi';
import FormationSanitaireApi from '../api/FormationSanitaireApi';

const useStyles = makeStyles(theme => ({
	root: {
		padding: theme.spacing(2),
		background: 'linear-gradient(145deg, #f5f9f7 0%, #edf5ff 100%)',
		borderRadius: '18px',
		[theme.breakpoints.up('md')]: {
			padding: theme.spacing(3),
		},
	},
	shell: {
		maxWidth: '1180px',
		margin: '0 auto',
	},
	card: {
		padding: theme.spacing(3),
		borderRadius: '18px',
		background: 'rgba(255, 255, 255, 0.92)',
		border: '1px solid rgba(16, 66, 52, 0.08)',
		boxShadow: '0 18px 40px rgba(9, 47, 36, 0.12)',
	},
	eyebrow: {
		margin: 0,
		color: '#0f5132',
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		fontSize: '0.78rem',
		fontWeight: 800,
	},
	title: {
		marginTop: theme.spacing(0.6),
		marginBottom: theme.spacing(0.8),
		fontWeight: 700,
		color: '#1a2f2a',
		fontFamily: 'Poppins, Nunito, sans-serif',
	},
	subtitle: {
		marginTop: 0,
		marginBottom: theme.spacing(2),
		color: '#45625a',
		lineHeight: 1.5,
		fontSize: '0.95rem',
	},
	field: {
		'& .MuiInputBase-root': {
			borderRadius: '12px',
			background: '#ffffff',
		},
	},
	actionsRow: {
		marginTop: theme.spacing(1),
		display: 'flex',
		gap: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryBtn: {
		textTransform: 'none',
		fontWeight: 700,
		padding: theme.spacing(1.1, 3),
		borderRadius: '999px',
		background: 'linear-gradient(90deg, #0f766e 0%, #0f9a6d 100%)',
		boxShadow: '0 8px 24px rgba(15, 118, 110, 0.3)',
		'&:hover': {
			background: 'linear-gradient(90deg, #0d665f 0%, #0e8b61 100%)',
		},
	},
	secondaryBtn: {
		textTransform: 'none',
		fontWeight: 700,
		padding: theme.spacing(1.1, 3),
		borderRadius: '999px',
	},
	tableWrap: {
		marginTop: theme.spacing(2),
		borderRadius: '14px',
		border: '1px solid rgba(16, 66, 52, 0.12)',
		overflow: 'hidden',
	},
	headCell: {
		fontWeight: 700,
		color: '#1a2f2a',
		background: '#f3faf6',
	},
	empty: {
		padding: theme.spacing(2),
		textAlign: 'center',
		color: '#5f6f68',
	},
	info: {
		marginTop: theme.spacing(1),
		color: '#5f6f68',
		fontSize: '0.9rem',
	},
	rowActions: {
		display: 'flex',
		gap: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	smallBtn: {
		textTransform: 'none',
		fontWeight: 700,
		minWidth: 'auto',
		padding: theme.spacing(0.45, 1),
	},
	dangerBtn: {
		textTransform: 'none',
		fontWeight: 700,
		minWidth: 'auto',
		padding: theme.spacing(0.45, 1),
		color: '#b91c1c',
		borderColor: 'rgba(185, 28, 28, 0.35)',
	},
	inlineField: {
		'& .MuiInputBase-root': {
			fontSize: '0.85rem',
		},
	},
	iconBtn: {
		minWidth: 'auto',
		padding: theme.spacing(0.45, 0.75),
	},
	error: {
		marginTop: theme.spacing(1),
		color: '#b91c1c',
		fontSize: '0.9rem',
	}
}));

const EditIcon = (props) => (
	<SvgIcon {...props} viewBox="0 0 24 24">
		<path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 5.63a1 1 0 000-1.41L18.78 2.3a1 1 0 00-1.41 0l-1.25 1.25 3.75 3.75 1.24-1.67z" />
	</SvgIcon>
);

const DeleteIcon = (props) => (
	<SvgIcon {...props} viewBox="0 0 24 24">
		<path d="M6 7h12v2H6V7zm2 3h8v9H8v-9zm3-7h2l1 1h5v2H5V4h5l1-1z" />
	</SvgIcon>
);

const normalizeList = (data) => {
	if (!data) return [];
	if (Array.isArray(data)) return data;
	if (typeof data.toArray === 'function') return data.toArray();
	return [];
};

const MembresContainer = memo(() => {
	const classes = useStyles();
	const [membres, setMembres] = useState([]);
	const [chargement, setChargement] = useState(false);
	const [exportEnCours, setExportEnCours] = useState(false);
	const [sauvegardeEnCours, setSauvegardeEnCours] = useState(false);
	const [erreur, setErreur] = useState('');
	const [filtres, setFiltres] = useState({
		nom: '',
		prenom: '',
		structure: '',
	});
	const [editionId, setEditionId] = useState('');
	const [editionForm, setEditionForm] = useState({
		nom: '',
		prenom: '',
		structure: '',
		dateInscription: '',
		idStructure: '',
	});
	const [formationsSanitaires, setFormationsSanitaires] = useState([]);

	const chargerMembres = async (filtresCourants = filtres) => {
		setChargement(true);
		setErreur('');

		try {
			const data = await MembreApi.obtenirToutesLesMembres();

			setMembres(normalizeList(data));
		} catch (error) {
			setErreur('Impossible de charger la liste des membres.');
			setMembres([]);
		} finally {
			setChargement(false);
		}
	};

	useEffect(() => {
		chargerMembres();
	}, []);

	useEffect(() => {
		const chargerFormationsSanitaires = async () => {
			try {
				const data = await FormationSanitaireApi.obtenirToutesLesFormationsSanitaires();
				const liste = Array.isArray(data) ? data : (typeof (data as any)?.toArray === 'function' ? (data as any).toArray() : []);
				setFormationsSanitaires(liste);
			} catch (error) {
				setFormationsSanitaires([]);
			}
		};

		chargerFormationsSanitaires();
	}, []);

	const handleChange = (event) => {
		const { name, value } = event.target;

		setFiltres(prev => ({ ...prev, [name]: value }));
	};

	const reinitialiserFiltres = () => {
		const init = {
			nom: '',
			prenom: '',
			structure: '',
		};

		setFiltres(init);
		chargerMembres(init);
	};

	const appliquerFiltres = (event) => {
		event.preventDefault();
		// Le filtrage est applique cote front pour garantir le fonctionnement meme si l'API ignore les query params.
	};

	const telechargerBlob = (blob, filename) => {
		const url = window.URL.createObjectURL(blob);
		const lien = document.createElement('a');
		lien.href = url;
		lien.download = filename;
		document.body.appendChild(lien);
		lien.click();
		document.body.removeChild(lien);
		window.URL.revokeObjectURL(url);
	};

	const exporterExcel = async () => {
		setExportEnCours(true);
		setErreur('');

		try {
			const { blob, nomFichier } = await MembreApi.exporterMembresExcel({
				nom: filtres.nom,
				prenom: filtres.prenom,
				structure: filtres.structure,
			});

			telechargerBlob(blob, nomFichier || 'membres.xlsx');
		} catch (error) {
			setErreur('Export Excel backend indisponible pour le moment.');
		} finally {
			setExportEnCours(false);
		}
	};

	const commencerEdition = (membre) => {
		if (editionId && editionId !== String(membre?.id || '')) {
			return;
		}

		setErreur('');
		setEditionId(String(membre?.id || ''));
		setEditionForm({
			nom: membre?.nom || '',
			prenom: membre?.prenom || '',
			structure: membre?.structure || '',
			dateInscription: membre?.dateInscription ? membre.dateInscription.format('YYYY-MM-DD') : '',
			idStructure: membre?.idStructure || '',
		});
	};

	const annulerEdition = () => {
		setEditionId('');
		setEditionForm({
			nom: '',
			prenom: '',
			structure: '',
			dateInscription: '',
			idStructure: '',
		});
	};

	const changerEdition = (event) => {
		const { name, value } = event.target;

		if (name === 'idStructure') {
			const formationChoisie = formationsSanitaires.find((formation) => String(formation?.id || '') === String(value || ''));
			setEditionForm(prev => ({
				...prev,
				idStructure: value,
				structure: formationChoisie?.nom || prev.structure,
			}));
			return;
		}

		setEditionForm(prev => ({ ...prev, [name]: value }));
	};

	const sauvegarderEdition = async (id) => {
		if (!editionForm.nom.trim() || !editionForm.prenom.trim()) {
			setErreur('Nom et prenom sont obligatoires pour modifier un membre.');
			return;
		}

		setSauvegardeEnCours(true);
		setErreur('');
		try {
			await MembreApi.modifierMembre(id, {
				nom: editionForm.nom.trim(),
				prenom: editionForm.prenom.trim(),
				structure: editionForm.structure.trim(),
				idStructure: editionForm.idStructure,
				dateInscription: editionForm.dateInscription || null,
			});
			annulerEdition();
			await chargerMembres();
		} catch (error) {
			setErreur('Echec de la modification du membre.');
		} finally {
			setSauvegardeEnCours(false);
		}
	};

	const supprimerMembre = async (id) => {
		if (!id) return;

		const confirme = window.confirm('Confirmer la suppression de ce membre ?');
		if (!confirme) return;

		setErreur('');
		try {
			await MembreApi.supprimerMembre(id);
			if (editionId === String(id)) {
				annulerEdition();
			}
			await chargerMembres();
		} catch (error) {
			setErreur('Echec de suppression du membre.');
		}
	};

	const membresAffiches = useMemo(() => {
		const source = normalizeList(membres);
		const nom = filtres.nom.trim().toLowerCase();
		const prenom = filtres.prenom.trim().toLowerCase();
		const structure = filtres.structure.trim().toLowerCase();

		return source.filter((membre) => {
			const nomValeur = String(membre?.nom || '').toLowerCase();
			const prenomValeur = String(membre?.prenom || '').toLowerCase();
			const structureValeur = String(membre?.structure || '').toLowerCase();

			const nomOk = !nom || nomValeur.includes(nom);
			const prenomOk = !prenom || prenomValeur.includes(prenom);
			const structureOk = !structure || structureValeur.includes(structure);

			return nomOk && prenomOk && structureOk;
		});
	}, [membres, filtres]);
	const verrouEditionActif = Boolean(editionId);

	return (
		<div className={classes.root}>
			<div className={classes.shell}>
				<Paper className={classes.card} elevation={0}>
					<p className={classes.eyebrow}>Repertoire</p>
					<h2 className={classes.title}>Tableau des membres</h2>
					<p className={classes.subtitle}>
						Filtrez les membres par nom, prenom et structure.
					</p>
					<Divider style={{ marginBottom: '20px' }} />

					<form onSubmit={appliquerFiltres}>
						<Grid container spacing={2.2}>
							<Grid item xs={12} md={3}>
								<TextField className={classes.field} label="Nom" name="nom" value={filtres.nom} onChange={handleChange} fullWidth />
							</Grid>
							<Grid item xs={12} md={3}>
								<TextField className={classes.field} label="Prenom" name="prenom" value={filtres.prenom} onChange={handleChange} fullWidth />
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField className={classes.field} label="Structure" name="structure" value={filtres.structure} onChange={handleChange} fullWidth />
							</Grid>

							<Grid item xs={12} className={classes.actionsRow}>
								<Button type="button" variant="outlined" className={classes.secondaryBtn} onClick={reinitialiserFiltres}>
									Reinitialiser
								</Button>
								<Button type="submit" variant="contained" color="primary" className={classes.primaryBtn} disabled={chargement}>
									{chargement ? 'Chargement...' : 'Filtrer'}
								</Button>
								<Button
									type="button"
									variant="contained"
									color="secondary"
									className={classes.secondaryBtn}
									onClick={exporterExcel}
									disabled={exportEnCours}
								>
									{exportEnCours ? 'Export...' : 'Exporter Excel'}
								</Button>
							</Grid>
						</Grid>
					</form>

					<Typography className={classes.info}>
						{membresAffiches.length} membre(s) trouve(s)
					</Typography>

					{erreur && <Typography className={classes.error}>{erreur}</Typography>}

					<TableContainer component={Paper} elevation={0} className={classes.tableWrap}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell className={classes.headCell}>Nom</TableCell>
									<TableCell className={classes.headCell}>Prenom</TableCell>
									<TableCell className={classes.headCell}>Structure</TableCell>
									<TableCell className={classes.headCell}>Date d'inscription</TableCell>
									<TableCell className={classes.headCell} align="right">Actions</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{membresAffiches.length === 0 && (
									<TableRow>
										<TableCell colSpan={5} className={classes.empty}>
											Aucun membre a afficher.
										</TableCell>
									</TableRow>
								)}

								{membresAffiches.map((membre) => (
									<TableRow key={membre.id || `${membre.nom}-${membre.prenom}`}>
										<TableCell>
											{editionId === String(membre.id)
												? <TextField className={classes.inlineField} size="small" name="nom" value={editionForm.nom} onChange={changerEdition} fullWidth />
												: membre.nom}
										</TableCell>
										<TableCell>
											{editionId === String(membre.id)
												? <TextField className={classes.inlineField} size="small" name="prenom" value={editionForm.prenom} onChange={changerEdition} fullWidth />
												: membre.prenom}
										</TableCell>
										<TableCell>
											{editionId === String(membre.id)
													? (
														<TextField
															className={classes.inlineField}
															size="small"
															name="idStructure"
															value={editionForm.idStructure}
															onChange={changerEdition}
															fullWidth
															select
														>
															{formationsSanitaires.map((formation) => (
																<MenuItem key={formation.id || formation.nom} value={formation.id}>
																	{formation.nom}
																</MenuItem>
															))}
														</TextField>
													)
												: (membre.structure || '-')}
										</TableCell>
										<TableCell>
											{editionId === String(membre.id)
												? <TextField className={classes.inlineField} size="small" type="date" name="dateInscription" value={editionForm.dateInscription} onChange={changerEdition} InputLabelProps={{ shrink: true }} fullWidth />
												: (membre.dateInscription ? new Date(membre.dateInscription).toLocaleDateString('fr-FR') : '-')}
										</TableCell>
										<TableCell align="right">
											<div className={classes.rowActions}>
												{editionId === String(membre.id) ? (
													<>
														<Button size="small" variant="contained" color="primary" className={classes.smallBtn} onClick={() => sauvegarderEdition(membre.id)} disabled={sauvegardeEnCours}>
															{sauvegardeEnCours ? '...' : 'Sauvegarder'}
														</Button>
														<Button size="small" variant="outlined" className={classes.smallBtn} onClick={annulerEdition} disabled={sauvegardeEnCours}>
															Annuler
														</Button>
													</>
												) : (
													<>
														<Button size="small" variant="outlined" className={classes.iconBtn} onClick={() => commencerEdition(membre)} disabled={verrouEditionActif && editionId !== String(membre.id)} aria-label="Modifier">
															<EditIcon fontSize="small" />
														</Button>
														<Button size="small" variant="outlined" className={`${classes.iconBtn} ${classes.dangerBtn}`} onClick={() => supprimerMembre(membre.id)} disabled={verrouEditionActif && editionId !== String(membre.id)} aria-label="Supprimer">
															<DeleteIcon fontSize="small" />
														</Button>
													</>
												)}
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			</div>
		</div>
	);
});

export default MembresContainer;
